import { createContext, useContext, useId, type AriaAttributes, type InputHTMLAttributes, type ReactNode, type Ref, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { ChevronDown, CircleAlert } from 'lucide-react';
import { cx } from '@/lib/cx';

interface FieldCtx {
  id: string;
  describedBy?: string;
  invalid: boolean;
  required: boolean;
}
const Ctx = createContext<FieldCtx | null>(null);

/** Wires an input inside <Field> to its label, help, error and required state. */
export function useFieldControl(props: { id?: string; 'aria-describedby'?: string; 'aria-invalid'?: AriaAttributes['aria-invalid']; required?: boolean }) {
  const f = useContext(Ctx);
  return {
    id: props.id ?? f?.id,
    'aria-describedby': cx(props['aria-describedby'], f?.describedBy) || undefined,
    'aria-invalid': props['aria-invalid'] ?? (f?.invalid ? true : undefined),
    required: props.required ?? f?.required,
  };
}

export interface FieldProps {
  label: ReactNode;
  children: ReactNode;
  help?: ReactNode;
  error?: string | null;
  required?: boolean;
  optional?: boolean;
  /** Character counter, e.g. { value: text.length, max: 500 }. */
  counter?: { value: number; max: number };
  id?: string;
  className?: string;
  /** Visually hide the label (still announced). */
  hideLabel?: boolean;
  /** Place label and control on one row (switches/checkbox settings). */
  inline?: boolean;
}

/**
 * Field wrapper: label → control → help/error. Errors replace help text and are announced.
 * Required fields show a subtle asterisk plus screen-reader text; optional fields say "Optional".
 */
export function Field({ label, children, help, error, required, optional, counter, id: idProp, className, hideLabel, inline }: FieldProps) {
  const auto = useId();
  const id = idProp ?? `f-${auto}`;
  const helpId = help ? `${id}-help` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const countId = counter ? `${id}-count` : undefined;
  const over = counter ? counter.value > counter.max : false;
  return (
    <Ctx.Provider value={{ id, describedBy: cx(errId, helpId, countId) || undefined, invalid: !!error || over, required: !!required }}>
      <div className={cx('field', inline && 'field-inline', !!error && 'is-invalid', className)}>
        <div className={cx('field-label-row', hideLabel && 'sr-only')}>
          <label htmlFor={id} className="field-label">
            {label}
            {required && (
              <>
                <span className="field-required" aria-hidden>*</span>
                <span className="sr-only"> (required)</span>
              </>
            )}
          </label>
          {optional && !required && <span className="field-optional">Optional</span>}
        </div>
        <div className="field-control">{children}</div>
        {(error || help || counter) && (
          <div className="field-meta">
            {error ? (
              <p id={errId} className="field-error" role="alert">
                <CircleAlert size={14} aria-hidden />
                <span>{error}</span>
              </p>
            ) : help ? (
              <p id={helpId} className="field-help">{help}</p>
            ) : (
              <span />
            )}
            {counter && (
              <span id={countId} className={cx('field-counter', over && 'is-over')} aria-live="polite">
                {counter.value}/{counter.max}
              </span>
            )}
          </div>
        )}
      </div>
    </Ctx.Provider>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: 'sm' | 'md' | 'lg';
  leading?: ReactNode;
  trailing?: ReactNode;
  ref?: Ref<HTMLInputElement>;
}

export function Input({ size = 'md', leading, trailing, className, ref, ...rest }: InputProps) {
  const a11y = useFieldControl(rest);
  const input = <input ref={ref} className={cx('input', `input-${size}`, !!leading && 'has-leading', !!trailing && 'has-trailing', !(leading || trailing) && className)} {...rest} {...a11y} />;
  if (!leading && !trailing) return input;
  return (
    <div className={cx('input-wrap', className)}>
      {leading && <span className="input-adorn input-adorn-leading" aria-hidden>{leading}</span>}
      {input}
      {trailing && <span className="input-adorn input-adorn-trailing">{trailing}</span>}
    </div>
  );
}

export function Textarea({ className, rows = 4, ref, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { ref?: Ref<HTMLTextAreaElement> }) {
  const a11y = useFieldControl(rest);
  return <textarea ref={ref} rows={rows} className={cx('input', 'textarea', className)} {...rest} {...a11y} />;
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  options: SelectOption[];
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  ref?: Ref<HTMLSelectElement>;
}

/** Native select (best on mobile & for accessibility), styled to match inputs. */
export function Select({ options, placeholder, size = 'md', className, ref, ...rest }: SelectProps) {
  const a11y = useFieldControl(rest);
  return (
    <div className={cx('select-wrap', className)}>
      <select ref={ref} className={cx('input', 'select', `input-${size}`)} {...rest} {...a11y}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="select-chevron" size={16} aria-hidden />
    </div>
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  description?: ReactNode;
  indeterminate?: boolean;
}

export function Checkbox({ label, description, className, indeterminate, id: idProp, ...rest }: CheckboxProps) {
  const auto = useId();
  const id = idProp ?? `cb-${auto}`;
  return (
    <div className={cx('check', className)}>
      <input
        id={id}
        type="checkbox"
        className="check-input"
        ref={(el) => {
          if (el) el.indeterminate = !!indeterminate;
        }}
        aria-describedby={description ? `${id}-d` : undefined}
        {...rest}
      />
      <label htmlFor={id} className="check-label">
        <span className="check-title">{label}</span>
        {description && <span id={`${id}-d`} className="check-desc">{description}</span>}
      </label>
    </div>
  );
}

export interface RadioGroupProps<T extends string> {
  name: string;
  value: T | undefined;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: ReactNode; description?: ReactNode; disabled?: boolean }>;
  legend: ReactNode;
  hideLegend?: boolean;
  orientation?: 'vertical' | 'horizontal';
  variant?: 'plain' | 'cards';
  error?: string;
}

/** Native radios in a fieldset: arrow-key navigation comes for free. */
export function RadioGroup<T extends string>({ name, value, onChange, options, legend, hideLegend, orientation = 'vertical', variant = 'plain', error }: RadioGroupProps<T>) {
  const auto = useId();
  return (
    <fieldset className={cx('radio-group', `radio-${orientation}`, `radio-${variant}`, !!error && 'is-invalid')} aria-describedby={error ? `${auto}-err` : undefined}>
      <legend className={cx('field-label', hideLegend && 'sr-only')}>{legend}</legend>
      <div className="radio-options">
        {options.map((o) => {
          const id = `${auto}-${o.value}`;
          return (
            <div key={o.value} className={cx('check', 'radio', value === o.value && 'is-checked', o.disabled && 'is-disabled')}>
              <input id={id} type="radio" name={name} value={o.value} checked={value === o.value} disabled={o.disabled} onChange={() => onChange(o.value)} className="check-input" />
              <label htmlFor={id} className="check-label">
                <span className="check-title">{o.label}</span>
                {o.description && <span className="check-desc">{o.description}</span>}
              </label>
            </div>
          );
        })}
      </div>
      {error && (
        <p id={`${auto}-err`} className="field-error" role="alert">
          <CircleAlert size={14} aria-hidden />
          {error}
        </p>
      )}
    </fieldset>
  );
}

export interface SwitchProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
}

export function Switch({ checked, onChange, label, description, disabled, id: idProp }: SwitchProps) {
  const auto = useId();
  const id = idProp ?? `sw-${auto}`;
  return (
    <div className={cx('switch-row', disabled && 'is-disabled')}>
      <div className="switch-text">
        <label htmlFor={id} className="check-title">{label}</label>
        {description && <span id={`${id}-d`} className="check-desc">{description}</span>}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={description ? `${id}-d` : undefined}
        disabled={disabled}
        className={cx('switch', checked && 'is-on')}
        onClick={() => onChange(!checked)}
      >
        <span className="switch-thumb" />
      </button>
    </div>
  );
}

/** Native date input (YYYY-MM-DD), styled. Keyboard + mobile pickers are handled by the browser. */
export function DateInput(props: Omit<InputProps, 'type'>) {
  return <Input type="date" {...props} className={cx('input-date', props.className)} />;
}
